import { useCallback, useRef } from 'react'
import { toast } from 'react-hot-toast'
import * as Sentry from '@sentry/react'
import { useInvoice } from '@/contexts/InvoiceContext'
import { api, ApiError } from '@/services/api'
import { Invoice, EmailTemplate, ArchiveEntry } from '@/types/invoice'
import { formatCurrency, getDaysOverdue } from '@/lib/utils'

export function useInvoiceOperations() {
  const { state, dispatch, setInvoices, setLoading, setError, clearError, addToArchive, restoreFromArchive, updateEmailTemplate } = useInvoice()
  
  // Use ref to store updateEmailTemplate to avoid dependency issues
  const updateEmailTemplateRef = useRef(updateEmailTemplate)
  updateEmailTemplateRef.current = updateEmailTemplate

  // Parse uploaded file
  const parseFile = useCallback(async (files: File[]) => {
    if (files.length === 0) return

    const file = files[0]
    setLoading(true)
    clearError()

    // Set Sentry context
    Sentry.setContext('file_upload', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      timestamp: new Date().toISOString()
    })

    try {
      const response = await api.parseFile(file)
      
      if (response.success) {
        // Save invoices to database
        const saveResponse = await api.saveInvoices(response.invoices)
        if (saveResponse.success) {
          setInvoices(saveResponse.invoices || [])
          toast.success(`Successfully parsed and saved ${response.invoices.length} invoices`)
        } else {
          setError('Failed to save invoices to database')
          toast.error('Failed to save invoices to database')
        }
        
        // Log success to Sentry
        Sentry.addBreadcrumb({
          category: 'file_upload',
          message: `Successfully parsed ${response.invoices.length} invoices from ${file.name}`,
          level: 'info'
        })
      } else {
        const errorMessage = response.errors?.join(', ') || 'Failed to parse file'
        setError(errorMessage)
        toast.error(errorMessage)
        
        // Capture error in Sentry
        Sentry.captureException(new Error(`Parse failed: ${errorMessage}`), {
          tags: {
            operation: 'file_parse',
            fileName: file.name,
            fileType: file.type
          },
          extra: {
            errors: response.errors,
            fileName: file.name,
            fileSize: file.size
          }
        })
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Error uploading file'
      
      setError(errorMessage)
      toast.error(errorMessage)
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          operation: 'file_upload',
          fileName: file.name
        },
        extra: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        }
      })
    } finally {
      setLoading(false)
    }
  }, [setInvoices, setLoading, setError, clearError])

  // Parse plain text
  const parseText = useCallback(async (text: string) => {
    // Enhanced validation - check for empty, whitespace-only, or very short text
    const trimmedText = text.trim()
    if (!trimmedText) {
      console.log('Text parsing skipped: Empty or whitespace-only text')
      return
    }
    
    if (trimmedText.length < 5) {
      console.log('Text parsing skipped: Text too short (minimum 5 characters)')
      toast.error('Text is too short. Please provide more content to parse.')
      return
    }

    setLoading(true)
    clearError()

    // Debug logging
    console.log('=== FRONTEND TEXT PARSE ===')
    console.log('Text being sent:', text)
    console.log('Text length:', text.length)

    // Set Sentry context
    Sentry.setContext('text_parse', {
      textLength: text.length,
      timestamp: new Date().toISOString()
    })

    try {
      const response = await api.parseText(text)
      
      if (response.success) {
        // Save invoices to database
        const saveResponse = await api.saveInvoices(response.invoices)
        if (saveResponse.success) {
          setInvoices(saveResponse.invoices || [])
          toast.success(`Successfully parsed and saved ${response.invoices.length} invoices from text`)
        } else {
          setError('Failed to save invoices to database')
          toast.error('Failed to save invoices to database')
        }
        
        // Log success to Sentry
        Sentry.addBreadcrumb({
          category: 'text_parse',
          message: `Successfully parsed ${response.invoices.length} invoices from text input`,
          level: 'info'
        })
      } else {
        const errorMessage = response.errors?.join(', ') || 'Failed to parse text'
        setError(errorMessage)
        toast.error(errorMessage)
        
        // Capture error in Sentry
        Sentry.captureException(new Error(`Text parse failed: ${errorMessage}`), {
          tags: {
            operation: 'text_parse'
          },
          extra: {
            errors: response.errors,
            textLength: text.length
          }
        })
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Error parsing text'
      
      setError(errorMessage)
      toast.error(errorMessage)
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          operation: 'text_parse'
        },
        extra: {
          textLength: text.length
        }
      })
    } finally {
      setLoading(false)
    }
  }, [setInvoices, setLoading, setError, clearError])

  // Generate email content from template
  const generateEmailContent = useCallback((invoice: Invoice, template: EmailTemplate): { subject: string; body: string } => {
    const daysOverdue = getDaysOverdue(invoice.dueDate)
    const amount = formatCurrency(invoice.amount, invoice.currency)
    const dueDate = new Date(invoice.dueDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    let subject = template.subject
      .replace(/{client}/g, invoice.client)
      .replace(/{number}/g, invoice.number)
      .replace(/{amount}/g, amount)
      .replace(/{dueDate}/g, dueDate)
      .replace(/{daysOverdue}/g, daysOverdue.toString())
      .replace(/{description}/g, invoice.description || '')

    let body = template.body
      .replace(/{client}/g, invoice.client)
      .replace(/{number}/g, invoice.number)
      .replace(/{amount}/g, amount)
      .replace(/{dueDate}/g, dueDate)
      .replace(/{daysOverdue}/g, daysOverdue.toString())
      .replace(/{description}/g, invoice.description || '')

    return { subject, body }
  }, [])

  // Open email in local email program
  const openEmail = useCallback((invoice: Invoice, template: EmailTemplate) => {
    try {
      const { subject, body } = generateEmailContent(invoice, template)
      const mailto = `mailto:${invoice.email && invoice.email !== 'Unknown' ? invoice.email : ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
      
      window.open(mailto, '_blank')
      toast.success('Opening email program...')
    } catch (error) {
      console.error('Open email error:', error)
      toast.error('Failed to open email program')
    }
  }, [generateEmailContent])

  // Archive invoice (mark as sent)
  const archiveInvoice = useCallback(async (invoice: Invoice, template: EmailTemplate, notes?: string) => {
    try {
      const { subject, body } = generateEmailContent(invoice, template)
      
      const archiveEntry: ArchiveEntry = {
        id: `archive_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        invoiceId: invoice.number,
        invoiceNumber: invoice.number,
        client: invoice.client,
        amount: invoice.amount,
        currency: invoice.currency,
        emailTemplateId: template.id,
        emailTemplateName: template.name,
        emailTemplateSubject: template.subject,
        emailTemplateBody: template.body,
        sentAt: new Date().toISOString(),
        status: 'sent',
        notes,
        emailSubject: subject,
        emailBody: body
      }
      
      // Save to database
      const response = await api.saveArchiveEntry(archiveEntry)
      if (response.success) {
        addToArchive(archiveEntry)
        // Remove the invoice from the main list when archived
        dispatch({ type: 'DELETE_INVOICE', payload: invoice.number })
        toast.success(`Invoice ${invoice.number} archived successfully`)
      } else {
        const errorMessage = response.error || 'Failed to save archive entry'
        console.error('Archive failed:', errorMessage)
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Archive error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive invoice'
      toast.error(errorMessage)
    }
  }, [generateEmailContent, addToArchive, dispatch])

  // Restore invoice from archive
  const restoreInvoice = useCallback((archiveId: string, invoice: Invoice) => {
    try {
      restoreFromArchive(archiveId, invoice)
      toast.success(`Invoice ${invoice.number} restored successfully`)
    } catch (error) {
      console.error('Restore error:', error)
      toast.error('Failed to restore invoice')
    }
  }, [restoreFromArchive])

  // Load invoices from database
  const loadInvoices = useCallback(async () => {
    setLoading(true)
    clearError()

    try {
      const response = await api.getInvoices()
      if (response.success) {
        setInvoices(response.invoices || [])
      } else {
        const errorMessage = response.error || 'Failed to load invoices'
        setError(errorMessage)
        toast.error(errorMessage)
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Error loading invoices'
      
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, []) // Remove dependencies to prevent infinite loops

  // Get invoice statistics from database
  const getInvoiceStats = useCallback(async () => {
    try {
      const stats = await api.getStatistics()
      return stats
    } catch (error) {
      console.error('Failed to get statistics:', error)
      // Fallback to local calculation
      const total = state.invoices.length
      const overdue = state.invoices.filter(inv => getDaysOverdue(inv.dueDate) > 0).length
      const totalAmount = state.invoices.reduce((sum, inv) => sum + inv.amount, 0)
      const overdueAmount = state.invoices
        .filter(inv => getDaysOverdue(inv.dueDate) > 0)
        .reduce((sum, inv) => sum + inv.amount, 0)

      return {
        totalInvoices: total,
        overdueInvoices: overdue,
        totalAmount,
        overdueAmount,
        collectionRate: total > 0 ? ((total - overdue) / total) * 100 : 0
      }
    }
  }, [state.invoices])

  // Load email templates from database
  const loadEmailTemplates = useCallback(async () => {
    try {
      const response = await api.getEmailTemplates()
      if (response.success && response.templates && response.templates.length > 0) {
        // Use the default template or the first one
        const defaultTemplate = response.templates.find((t: EmailTemplate) => t.isDefault) || response.templates[0]
        // Update the email template in context using ref
        updateEmailTemplateRef.current(defaultTemplate)
        console.log('Loaded and updated email template:', defaultTemplate)
        return defaultTemplate
      }
    } catch (error) {
      console.error('Failed to load email templates:', error)
    }
    return null
  }, []) // Empty dependency array to prevent infinite loops

  return {
    parseFile,
    parseText,
    loadInvoices,
    loadEmailTemplates,
    generateEmailContent,
    openEmail,
    archiveInvoice,
    restoreInvoice,
    getInvoiceStats,
    isLoading: state.isLoading,
    error: state.error,
    invoices: state.invoices
  }
} 